import { ProductUUID } from "namirasoft-api-product";
import { BaseColumnFormatter, DateTimeFormatter, IDFormatter, StringFormatter, StringFormatterSizeType } from "namirasoft-site-react";
import { IEntityInfo, NSARouterMakerProps } from "namirasoft-account-react";
import { CredentialRoutes } from "./CredentialRoutes";
import { AWSCreateRow, AWSRow, AzureCreateRow, AzureRow, GCPCreateRow, GCPRow } from "namirasoft-credential";
import { CredentialProps } from "./CredentialProps";
import { NamirasoftMap } from "namirasoft-site-map";
import { IStorageLocal } from "namirasoft-core";

export class CredentialEntities
{
	static aws(props: NSARouterMakerProps & CredentialProps): IEntityInfo<AWSRow, AWSCreateRow>
	{
		let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

		return {
			product_id: ProductUUID.NAMIRASOFT_CREDENTIAL,
			name: "aws",
			tables: [{ table: namirasoft.credential.tables.aws, required: true }],
			client: {
				getListURL: () => CredentialRoutes.AWSList,
				getNewURL: () => CredentialRoutes.AWSNew,
				getCopyURL: (id: string) => CredentialRoutes.AWSCopy(id),
				getEditURL: (id: string) => CredentialRoutes.AWSEdit(id),
				getViewURL: (id: string) => CredentialRoutes.AWSView(id),
				getColumns: () =>
				{
					return [
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.aws.columns.id, new IDFormatter(namirasoft, null)),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.aws.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.aws.columns.workspace_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.aws.columns.name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.aws.columns.access_key, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.aws.columns.secret_key, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.aws.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.aws.columns.created_at, new DateTimeFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.aws.columns.updated_at, new DateTimeFormatter()),
					];
				}
			},
			server: {
				value_list: props.servers.credential.value.List,
				get: props.servers.credential.aws.Get,
				list: props.servers.credential.aws.List,
				create: props.servers.credential.aws.Create,
				update: props.servers.credential.aws.Update,
				delete: props.servers.credential.aws.Delete,
			}
		};
	}

	static azure(props: NSARouterMakerProps & CredentialProps): IEntityInfo<AzureRow, AzureCreateRow>
	{
		let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

		return {
			product_id: ProductUUID.NAMIRASOFT_CREDENTIAL,
			name: "azure",
			tables: [{ table: namirasoft.credential.tables.azure, required: true }],
			client: {
				getListURL: () => CredentialRoutes.AzureList,
				getNewURL: () => CredentialRoutes.AzureNew,
				getCopyURL: (id: string) => CredentialRoutes.AzureCopy(id),
				getEditURL: (id: string) => CredentialRoutes.AzureEdit(id),
				getViewURL: (id: string) => CredentialRoutes.AzureView(id),
				getColumns: () =>
				{
					return [
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.id, new IDFormatter(namirasoft, null)),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.workspace_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.tenant_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.client_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.client_secret, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.created_at, new DateTimeFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.azure.columns.updated_at, new DateTimeFormatter()),
					];
				}
			},
			server: {
				value_list: props.servers.credential.value.List,
				get: props.servers.credential.azure.Get,
				list: props.servers.credential.azure.List,
				create: props.servers.credential.azure.Create,
				update: props.servers.credential.azure.Update,
				delete: props.servers.credential.azure.Delete,
			}
		};
	}

	static gcp(props: NSARouterMakerProps & CredentialProps): IEntityInfo<GCPRow, GCPCreateRow>
	{
		let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

		return {
			product_id: ProductUUID.NAMIRASOFT_CREDENTIAL,
			name: "gcp",
			tables: [{ table: namirasoft.credential.tables.gcp, required: true }],
			client: {
				getListURL: () => CredentialRoutes.GCPList,
				getNewURL: () => CredentialRoutes.GCPNew,
				getCopyURL: (id: string) => CredentialRoutes.GCPCopy(id),
				getEditURL: (id: string) => CredentialRoutes.GCPEdit(id),
				getViewURL: (id: string) => CredentialRoutes.GCPView(id),
				getColumns: () =>
				{
					return [
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.gcp.columns.id, new IDFormatter(namirasoft, null)),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.gcp.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.gcp.columns.workspace_id, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.gcp.columns.name, new StringFormatter(StringFormatterSizeType.TwoWords)),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.gcp.columns.credentials, new IDFormatter(namirasoft, new IStorageLocal())),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.gcp.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.gcp.columns.created_at, new DateTimeFormatter()),
						BaseColumnFormatter.getColumn(namirasoft.credential.tables.gcp.columns.updated_at, new DateTimeFormatter()),
					];
				}
			},
			server: {
				value_list: props.servers.credential.value.List,
				get: props.servers.credential.gcp.Get,
				list: props.servers.credential.gcp.List,
				create: props.servers.credential.gcp.Create,
				update: props.servers.credential.gcp.Update,
				delete: props.servers.credential.gcp.Delete,
			}
		};
	}
}